import {
    ActionTypes,
    doesWorkflowRejectCodeExist,
    getWorkflowRejectCodeMessage
} from '@tassat/common-definitions';
import { IFormValues } from '@tassat/forms/src/WithdrawalForm/types';
import {
    AccountWithdrawalDataRecord,
    CustomerAddAccountWithdrawalDataReq,
    CustomerModifyAccountWithdrawalDataReq,
    WithdrawalTypeEnum
} from '@tassat/fred-definitions';

import history from 'Lib/browserHistory';
import Worker from 'Lib/worker/PrimusWorker';

export const arePendingTransactions = async (instructionId: string): Promise<boolean> => {
    const pendingTransactions = {
        Condition: 'isCompleted|EQ|false:' +
            `AccountWithdrawalDataId|EQ|${instructionId}`,
        PageNo: 1,
        RecordPerPage: 4,
        Sort: 'auditTime|DESC'
    };

    try {
        const res = await Worker.getInstance()
            .getTransactions(pendingTransactions);

        if (res.data?.TotalCount > 0) {
            return true;
        }

        return false;
    } catch (error) {
        // INFO: Can't validate if anything pending so treat as if there are pending transactions

        return true;
    }
};

export const submitWithdrawalForm = async (values: IFormValues, opts: {
    actionType: ActionTypes;
    firmId: string;
    editWithdrawalInstruction: AccountWithdrawalDataRecord;
    onClose: () => void;
    setSubmitError: React.Dispatch<React.SetStateAction<string>>;
}) => {
    const {
        actionType,
        firmId,
        editWithdrawalInstruction,
        onClose,
        setSubmitError
    } = opts;

    try {
        let response: { data: any } = { data: undefined };

        if (actionType === ActionTypes.add) {
            const withdrawalInstructionValues: Omit<
                CustomerAddAccountWithdrawalDataReq,
                'RequestId' |
                'RequesterId' |
                'RequestType' |
                'RequestPattern' |
                'UUID' |
                'IsDefault' |
                'FirmId'
            > = {
                Description: values.Description.trim(),
                WithdrawalType: values.WithdrawalType.value.toString()
                    .toUpperCase() as WithdrawalTypeEnum,
                BeneName: values.BeneName.trim(),
                BeneBankAcct: values.BeneBankAcct.trim(),
                BeneStreetAddress: values.BeneStreetAddress.trim(),
                BeneCity: values.BeneCity.trim(),
                BeneStateProv: typeof values.BeneStateProv === 'object' ?
                    values.BeneStateProv.value.toString()
                    : values.BeneStateProv.trim(),
                BenePostalCode: values.BenePostalCode.trim(),
                BeneCountry: values.BeneCountry.trim(),
                BeneBankName: values.BeneBankName.trim(),
                BeneBankAccountType: values.BeneBankAccountType.value.toString(),
                BeneBankRoutNum: values.BeneBankRoutNum.trim(),
                BeneBankBic: values.BeneBankBic.trim(),
                BeneBankStreetAddress: values.BeneBankStreetAddress.trim(),
                BeneBankCity: values.BeneBankCity.trim(),
                BeneBankStateProv: typeof values.BeneBankStateProv === 'object' ?
                    values.BeneBankStateProv.value.toString()
                    : values.BeneBankStateProv.trim(),
                BeneBankPostalCode: values.BeneBankPostalCode.trim(),
                BeneBankCountry: values.BeneBankCountry.trim(),
                IntermediaryBankName: values.IntermediaryBankName.trim(),
                IntermediaryBankRoutNum: values.IntermediaryBankRoutNum.trim(),
                IntermediaryBankBic: values.IntermediaryBankBic.trim(),
                IntermediaryBankStreetAddress: values.IntermediaryBankStreetAddress.trim(),
                IntermediaryBankCity: values.IntermediaryBankCity.trim(),
                IntermediaryBankStateProv: typeof values.IntermediaryBankStateProv === 'object' ?
                    values.IntermediaryBankStateProv.value.toString()
                    : values.IntermediaryBankStateProv.trim(),
                IntermediaryBankPostalCode: values.IntermediaryBankPostalCode.trim(),
                IntermediaryBankCountry: values.IntermediaryBankCountry.trim(),
                BeneInfo1: values.BeneInfo1.trim(),
                BeneInfo2: values.BeneInfo2.trim(),
                BeneInfo3: values.BeneInfo3.trim(),
                BeneInfo4: values.BeneInfo4.trim(),
                BankToBankInfo1: values.BankToBankInfo1.trim(),
                BankToBankInfo2: values.BankToBankInfo2.trim(),
                BankToBankInfo3: values.BankToBankInfo3.trim(),
                BankToBankInfo4: values.BankToBankInfo4.trim(),
                Blockchain: values.Blockchain.value.toString(),
                Address: values.Address.trim()
            };

            response = await Worker.getInstance()
                .createWithdrawalInstruction(firmId, withdrawalInstructionValues);
        } else if (actionType === ActionTypes.edit) {
            const withdrawalInstructionValues: Omit<
                CustomerModifyAccountWithdrawalDataReq,
                'RequestId' |
                'RequesterId' |
                'RequestType' |
                'RequestPattern' |
                'UUID' |
                'FirmId'
            > = {
                AccountWithdrawalDataId: editWithdrawalInstruction.AccountWithdrawalDataId,
                IsDefault: editWithdrawalInstruction.IsDefault,
                Description: values.Description.trim(),
                WithdrawalType: values.WithdrawalType.value.toString()
                    .toUpperCase() as WithdrawalTypeEnum,
                BeneName: values.BeneName.trim(),
                BeneBankAcct: values.BeneBankAcct.trim(),
                BeneStreetAddress: values.BeneStreetAddress.trim(),
                BeneCity: values.BeneCity.trim(),
                BeneStateProv: typeof values.BeneStateProv === 'object' ?
                    values.BeneStateProv.value.toString()
                    : values.BeneStateProv.trim(),
                BenePostalCode: values.BenePostalCode.trim(),
                BeneCountry: values.BeneCountry.trim(),
                BeneBankName: values.BeneBankName.trim(),
                BeneBankAccountType: values.BeneBankAccountType.value.toString(),
                BeneBankRoutNum: values.BeneBankRoutNum.trim(),
                BeneBankBic: values.BeneBankBic.trim(),
                BeneBankStreetAddress: values.BeneBankStreetAddress.trim(),
                BeneBankCity: values.BeneBankCity.trim(),
                BeneBankStateProv: typeof values.BeneBankStateProv === 'object' ?
                    values.BeneBankStateProv.value.toString()
                    : values.BeneBankStateProv.trim(),
                BeneBankPostalCode: values.BeneBankPostalCode.trim(),
                BeneBankCountry: values.BeneBankCountry.trim(),
                IntermediaryBankName: values.IntermediaryBankName.trim(),
                IntermediaryBankRoutNum: values.IntermediaryBankRoutNum.trim(),
                IntermediaryBankBic: values.IntermediaryBankBic.trim(),
                IntermediaryBankStreetAddress: values.IntermediaryBankStreetAddress.trim(),
                IntermediaryBankCity: values.IntermediaryBankCity.trim(),
                IntermediaryBankStateProv: typeof values.IntermediaryBankStateProv === 'object' ?
                    values.IntermediaryBankStateProv.value.toString()
                    : values.IntermediaryBankStateProv.trim(),
                IntermediaryBankPostalCode: values.IntermediaryBankPostalCode.trim(),
                IntermediaryBankCountry: values.IntermediaryBankCountry.trim(),
                BeneInfo1: values.BeneInfo1.trim(),
                BeneInfo2: values.BeneInfo2.trim(),
                BeneInfo3: values.BeneInfo3.trim(),
                BeneInfo4: values.BeneInfo4.trim(),
                BankToBankInfo1: values.BankToBankInfo1.trim(),
                BankToBankInfo2: values.BankToBankInfo2.trim(),
                BankToBankInfo3: values.BankToBankInfo3.trim(),
                BankToBankInfo4: values.BankToBankInfo4.trim(),
                Blockchain: values.Blockchain.value.toString(),
                Address: values.Address.trim()
            };

            response = await Worker.getInstance()
                .updateWithdrawalInstruction(firmId, withdrawalInstructionValues);
        }

        if (response.data && response.data.Status === 48) {
            if (actionType === ActionTypes.edit) {
                history.push(`/admin/firms/${firmId}/withdrawals${response.data.NewRecordId ? `/${response.data.NewRecordId}` : ''}`);
            } else {
                onClose();
            }
        } else if (response.data && response.data.Status === 49) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : 'edit'} Withdrawal Instruction: ${response.data.Reason || 'Something went wrong'}`);
        } else if (response.data && doesWorkflowRejectCodeExist(response.data.Status)) {
            setSubmitError(`Could not ${actionType === ActionTypes.add ? 'add' : 'edit'} Withdrawal Instruction: ${getWorkflowRejectCodeMessage(response.data.Status)}`);
        }
    } catch (error) {
        setSubmitError('Something went wrong');
    }
};